/**
 * The platform HTTP client.
 *
 * Every service answers with the same envelope — `{ "data": … }` on success,
 * `{ "error": "…" }` on failure — so unwrapping belongs here and nowhere else.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly path: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /**
   * A capability refusal. The monitor ladder doing its job, not a broken
   * session — see the 401/403 rule in `request` below.
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export interface ApiClientOptions {
  /** Where to read the current access token from. */
  getToken(): string | null;
  /** Called on a 401 to obtain a new token. Return null to give up. */
  refresh?(): Promise<string | null>;
  /** Called when the session is definitively over. */
  onSignOut?(reason: string): void;
  /** Prefix for every request, e.g. "" (same origin) or a dev proxy target. */
  baseUrl?: string;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export class ApiClient {
  /**
   * Concurrent 401s must share ONE refresh. Without this, a dashboard that
   * fires six requests on load and finds an expired token would send six
   * refreshes — and since refresh tokens ROTATE, five of them would present a
   * token the server has already revoked, and the user would be signed out by
   * their own dashboard loading.
   */
  #refreshing: Promise<string | null> | null = null;

  constructor(private readonly opts: ApiClientOptions) {}

  get<T>(path: string): Promise<T> {
    return this.request<T>("GET", path);
  }
  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("POST", path, body);
  }
  /**
   * A POST that never carries the bearer token. For the credential endpoints
   * (login, refresh, logout): attaching a stale token to the very request
   * meant to replace it turns an ordinary expiry into a lockout — the server
   * rejects the dead bearer before ever reading the body.
   */
  postPublic<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("POST", path, body, true, false);
  }
  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("PUT", path, body);
  }
  /**
   * A PUT carrying a multipart form (file uploads). The browser must set the
   * content-type itself — it owns the boundary — so unlike the JSON methods
   * this one sets no content-type header.
   */
  putForm<T>(path: string, form: FormData): Promise<T> {
    return this.request<T>("PUT", path, form);
  }
  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("PATCH", path, body);
  }
  delete<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("DELETE", path, body);
  }

  async request<T>(
    method: Method,
    path: string,
    body?: unknown,
    retrying = false,
    withAuth = true,
  ): Promise<T> {
    const base = this.opts.baseUrl ?? "";
    const token = withAuth ? this.opts.getToken() : null;

    // FormData goes through untouched and WITHOUT a content-type header — the
    // browser writes the multipart boundary into the header itself.
    const isForm = typeof FormData !== "undefined" && body instanceof FormData;
    const res = await fetch(base + path, {
      method,
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(body !== undefined && !isForm ? { "content-type": "application/json" } : {}),
      },
      body: isForm ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });

    // 204, and any empty body, must not blow up on JSON.parse.
    const text = await res.text();
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
    }

    if (res.ok) {
      return (parsed as { data?: T } | null)?.data as T;
    }

    const message =
      (parsed as { error?: string } | null)?.error ?? text ?? `${res.status}`;

    // ── the rule, learned the hard way ────────────────────────────────────
    //
    // 401 means the token is gone or expired: try ONE refresh, then give up.
    //
    // 403 means the caller lacks a capability. That is the server enforcing
    // the model correctly — a supervisor pressing Barge without the capability
    // must see an inline refusal, NOT be thrown out of the application. Never
    // sign out on a 403, and never retry it.
    // A 401 is only a SESSION event when the request actually presented a
    // token. A 401 on a tokenless request (wrong password on login) is just
    // that request's answer — refreshing or signing out for it would replace
    // "invalid credentials" with a phantom "session expired".
    if (res.status === 401 && token) {
      if (!retrying && this.opts.refresh) {
        const fresh = await this.#sharedRefresh();
        if (fresh) {
          return this.request<T>(method, path, body, true, withAuth);
        }
      }
      this.opts.onSignOut?.("session expired");
    }

    throw new ApiError(res.status, message, path);
  }

  async #sharedRefresh(): Promise<string | null> {
    this.#refreshing ??= (async () => {
      try {
        return await this.opts.refresh!();
      } finally {
        this.#refreshing = null;
      }
    })();
    return this.#refreshing;
  }
}
